import { computed, MaybeRef, unref } from 'vue'
import { RateCardComparisonResult } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import {
  aboveMaximumMessage,
  RateCardBand,
  RateCardGuidance,
  rateCardTooltipText,
  useNoMatchGuidance,
  useRateCardGuidanceBandFields,
} from '@/components/Job/composables/rateCardGuidance'

export type { RateCardCriterion, RateCardBand, RateCardGuidance } from '@/components/Job/composables/rateCardGuidance'

// Pairs with useSupplierRateCardBand — reads the backend comparator's verdict directly.
export function useSupplierRateCardBandGuidance(options: {
  band: MaybeRef<RateCardBand>
  currencySymbol: MaybeRef<string>
  // Band still reflects the last-saved rate type — stale once unsaved.
  rateTypeIsUnsaved: MaybeRef<boolean>
  // True for a supplier job's band or the present-to-client modal — swaps the intro to "Your client's rate card".
  isClientBand?: MaybeRef<boolean>
}) {
  const noMatchGuidance = useNoMatchGuidance()

  const band = computed(() => unref(options.band))
  const rateTypeIsUnsaved = computed(() => unref(options.rateTypeIsUnsaved))
  const isClientBand = computed(() => unref(options.isClientBand) ?? false)

  const hasMatch = computed(() => band.value != null && band.value.noMatchReason == null)

  // Read from the backend comparator's verdict, not recomputed here.
  const comparison = computed(() => band.value?.comparison ?? null)
  const isUnitMismatch = computed(() => comparison.value === RateCardComparisonResult.UnitMismatch)

  // Whether the band may drive a warning/prefill, vs. only describing itself.
  const canActOnBand = computed(
    () => hasMatch.value && !rateTypeIsUnsaved.value && !isUnitMismatch.value,
  )

  const { rangeAmountText, unitText, validUntilText } = useRateCardGuidanceBandFields(
    band,
    options.currencySymbol,
  )

  const guidance = computed<RateCardGuidance | undefined>(() => {
    if (!band.value || rateTypeIsUnsaved.value) return undefined

    if (!hasMatch.value) return noMatchGuidance(band.value.noMatchReason)

    return {
      hasMatch: true,
      introText: isClientBand.value ? $t("Your client’s rate card suggests") : $t('Your rate card suggests'),
      rangeAmountText: rangeAmountText.value,
      unitText: unitText.value,
      excludesMspFee: false,
      validUntilText: validUntilText.value,
      criteria: band.value.criteria ?? [],
      tooltipText: rateCardTooltipText(isClientBand.value),
    }
  })

  const fieldWarning = (entered: string | number | undefined | null): string | undefined => {
    if (!canActOnBand.value) return undefined
    if (comparison.value !== RateCardComparisonResult.AboveMaximum) return undefined

    return aboveMaximumMessage(entered, band.value?.maximum, unref(options.currencySymbol))
  }

  return { canActOnBand, guidance, fieldWarning }
}
